/**
 * Per-site supplier code + trading currency for the Add Approved Supplier modal.
 *
 * Supplier code and currency are per-customer, not properties of the supplier —
 * the same code can mean different suppliers for different customers (aligns
 * with CR-6366). So once the buyer has chosen the site(s) this supplier will be
 * linked to, each selected site gets its own code + currency pair, written to
 * that site's relationship on submit.
 */
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2 } from 'lucide-react';

export interface SiteSetting {
  supplier_code: string;
  currency: string;
}

interface CustomerSiteSettingsProps {
  /** The sites currently chosen — one code+currency row is rendered per site. */
  sites: { id: number; name: string }[];
  settings: Record<number, SiteSetting>;
  onChange: (id: number, patch: Partial<SiteSetting>) => void;
  currencies: string[];
}

export default function CustomerSiteSettings({
  sites, settings, onChange, currencies,
}: CustomerSiteSettingsProps) {
  if (sites.length === 0) return null;
  return (
    <div className="space-y-2 rounded-md border px-3 py-3">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Supplier code &amp; currency per site</h4>
      </div>
      <p className="text-xs text-muted-foreground">
        Each customer keeps its own code and trading currency for this supplier.
      </p>
      <div className="space-y-3 pt-1">
        {sites.map((s) => {
          const val = settings[s.id] || { supplier_code: '', currency: 'GBP' };
          return (
            <div key={s.id} className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground truncate">{s.name}</Label>
                <Input
                  value={val.supplier_code}
                  onChange={(e) => onChange(s.id, { supplier_code: e.target.value })}
                  placeholder="Supplier code (optional)"
                />
              </div>
              <div className="space-y-1 sm:w-28">
                <Label className="text-xs text-muted-foreground">Currency</Label>
                <select
                  value={val.currency}
                  onChange={(e) => onChange(s.id, { currency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                >
                  {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
